import { Dispatch, FC, MouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldError, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { NewDropdown } from "@/components/Forms/Dropdown";
import { FacebookPage, OptimisationProjectResource } from "@/types/resources";
import { selectTeamTestingProperties } from "@/selectors";
import { RootState } from "@/utils/store";
import { customFacebookAudiences } from "@/utils/data";

enum Networks {
  FACEBOOK = 0,
  YOUTUBE = 1,
}

const networks = [
  {
    value: Networks.FACEBOOK,
    label: "Facebook",
  },
  {
    value: Networks.YOUTUBE,
    label: "Youtube",
  },
];

type FormDataT = {
  external_project_id?: string;
  type: number;
  name: string;
  network: Networks;
  owner: string;
  facebook_page_id?: string;
  custom_facebook_audience?: number;
};

interface Props {
  onClose: () => void;
  setActiveTab: Dispatch<
    React.SetStateAction<{ value: number; title: string }>
  >;
}

const OptimisationTestFormModal: FC<Props> = ({ onClose, setActiveTab }) => {
  const { teamId } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const { testingSettings } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!)
  );

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const { data: facebookPages } = useQuery<FacebookPage[]>(
    ["facebook_testing_pages"],
    () => api.get<never, FacebookPage[]>("/v1/facebook-testing-pages")
  );

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      type: 0,
      network: Networks.YOUTUBE,
      facebook_page_id: testingSettings?.facebook_page_id?.toString(),
      custom_facebook_audience: testingSettings?.custom_facebook_audience,
    },
  });

  const network = useWatch({ control, name: "network" });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { id } = await api.post<object, OptimisationProjectResource>(
        "/v1/optimisation-tests",
        {
          ...data,
          team_id: teamId === "all" ? null : teamId,
        }
      );

      navigate(`/teams/${teamId}/optimisation-projects/${id}`);

      alerts.success(`Optimisation test was successfully created`);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="New optimisation test" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="idInput">Project ID</Label>
                <Input
                  {...register("external_project_id")}
                  id="idInput"
                  type="text"
                  placeholder="Add project ID"
                />
              </Larger>
              <Larger>
                <Label htmlFor="nameInput">Project name</Label>
                <Input
                  $hasError={!!errors.name}
                  id="nameInput"
                  type="text"
                  placeholder="Add project name"
                  {...register("name")}
                />
                {!!errors.name && (
                  <ValidationMessage>{errors.name.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="ownerInput">Owner / Director</Label>
                <Input
                  {...register("owner")}
                  $hasError={!!errors.owner}
                  id="ownerInput"
                  type="text"
                  placeholder="Add owner name"
                />
                {!!errors.owner && (
                  <ValidationMessage>{errors.owner.message}</ValidationMessage>
                )}
              </Larger>
              <Larger onClick={(e) => e.stopPropagation()}>
                <Label htmlFor="networkSelect">Network</Label>
                <NewDropdown<Networks>
                  options={networks}
                  control={control}
                  name="network"
                  id="networkSelect"
                />
                {!!errors.network && (
                  <ValidationMessage>
                    {(errors.network as FieldError).message}
                  </ValidationMessage>
                )}
              </Larger>
              {network === Networks.FACEBOOK && (
                <>
                  <Larger>
                    <Label htmlFor="facebookChannelSelect">Facebook page</Label>
                    <NewDropdown<string>
                      options={
                        facebookPages?.map((page) => ({
                          value: page.id,
                          label: page.name,
                        })) ?? []
                      }
                      menuPlacement="top"
                      control={control}
                      name="facebook_page_id"
                      id="facebookChannelSelect"
                    />
                    {!!errors.facebook_page_id && (
                      <ValidationMessage>
                        {errors.facebook_page_id.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                  <Larger>
                    <Label htmlFor="facebookAudienceSelect">
                      Audience interests
                    </Label>
                    <NewDropdown<null | number>
                      options={customFacebookAudiences}
                      control={control}
                      menuPlacement="top"
                      name="custom_facebook_audience"
                      id="facebookAudienceSelect"
                    />
                    {!!errors.custom_facebook_audience && (
                      <ValidationMessage>
                        {errors.custom_facebook_audience.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                </>
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Create
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default OptimisationTestFormModal;
